import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { garageSpecialRequestService } from '../../services/apiService';

const SCOPE_OPTIONS = {
  exteriorBody: ['Full respray', 'Widebody / fender', 'Lighting conversion', 'Wheel & tyre package'],
  interiorComfort: ['Full retrim', 'Custom seats', 'Infotainment upgrade', 'Sound deadening'],
  engineDrivetrain: ['Engine swap', 'Forced induction', 'Transmission conversion', 'Exhaust system'],
  suspensionChassis: ['Air ride / coilovers', 'Frame restoration', 'Chassis stiffening', 'Brake overhaul'],
  electricalElectronics: ['Custom wiring harness', 'ECU controls', 'ADAS/camera integration', 'App controls'],
};

export default function TransformationProjectForm() {
  const [searchParams] = useSearchParams();
  const garageId = searchParams.get('garageId') || '';
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    projectNickname: '',
    make: '',
    model: '',
    year: '',
    condition: 'unknown',
    vision: '',
    budgetRange: '',
    stagedPaymentPreference: '30-30-40',
    selectedScope: {
      exteriorBody: [],
      interiorComfort: [],
      engineDrivetrain: [],
      suspensionChassis: [],
      electricalElectronics: [],
    },
  });

  const canSubmit = useMemo(() => form.fullName.trim() && form.phone.trim(), [form.fullName, form.phone]);

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleScope = (group, item) => {
    setForm((prev) => {
      const existing = prev.selectedScope[group] || [];
      const next = existing.includes(item) ? existing.filter((x) => x !== item) : [...existing, item];
      return { ...prev, selectedScope: { ...prev.selectedScope, [group]: next } };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await garageSpecialRequestService.createTransformationProject({
        garageId,
        clientInfo: {
          fullName: form.fullName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          projectNickname: form.projectNickname.trim(),
        },
        donorVehicle: {
          make: form.make.trim(),
          model: form.model.trim(),
          year: form.year.trim(),
          condition: form.condition,
        },
        vision: {
          description: form.vision.trim(),
        },
        scope: form.selectedScope,
        timelineBudget: {
          budgetRange: form.budgetRange.trim(),
          stagedPaymentPreference: form.stagedPaymentPreference,
        },
      });
      toast.success('Project intake submitted successfully.');
      setForm((prev) => ({
        ...prev,
        fullName: '',
        phone: '',
        email: '',
        projectNickname: '',
        make: '',
        model: '',
        year: '',
        condition: 'unknown',
        vision: '',
        budgetRange: '',
        stagedPaymentPreference: '30-30-40',
        selectedScope: {
          exteriorBody: [],
          interiorComfort: [],
          engineDrivetrain: [],
          suspensionChassis: [],
          electricalElectronics: [],
        },
      }));
    } catch (error) {
      toast.error(error.message || 'Failed to submit project intake');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <h1 className="text-2xl font-bold text-gray-900">Transformer Project Intake</h1>
      <p className="mt-2 text-sm text-gray-600">
        Submit a staged project brief. Team will review feasibility and share milestone quote.
      </p>

      <form onSubmit={handleSubmit} className="mt-5 space-y-5 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Full name *" value={form.fullName} onChange={(e) => updateField('fullName', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Phone *" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Email" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Project nickname" value={form.projectNickname} onChange={(e) => updateField('projectNickname', e.target.value)} />
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Make" value={form.make} onChange={(e) => updateField('make', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Model" value={form.model} onChange={(e) => updateField('model', e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Year" value={form.year} onChange={(e) => updateField('year', e.target.value)} />
          <select className="rounded-lg border px-3 py-2 text-sm" value={form.condition} onChange={(e) => updateField('condition', e.target.value)}>
            <option value="unknown">Condition</option>
            <option value="running">Running</option>
            <option value="non_running">Non-running</option>
            <option value="accident_damaged">Accident damaged</option>
          </select>
        </div>

        {Object.entries(SCOPE_OPTIONS).map(([group, items]) => (
          <div key={group}>
            <p className="mb-2 text-sm font-semibold capitalize text-gray-800">{group.replace(/([A-Z])/g, ' $1')}</p>
            <div className="flex flex-wrap gap-2">
              {items.map((item) => {
                const active = form.selectedScope[group].includes(item);
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleScope(group, item)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700'}`}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <textarea
          className="min-h-28 w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Transformation vision and key requirements"
          value={form.vision}
          onChange={(e) => updateField('vision', e.target.value)}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Budget range (e.g. 8L - 12L)" value={form.budgetRange} onChange={(e) => updateField('budgetRange', e.target.value)} />
          <select className="rounded-lg border px-3 py-2 text-sm" value={form.stagedPaymentPreference} onChange={(e) => updateField('stagedPaymentPreference', e.target.value)}>
            <option value="30-30-40">30%-30%-40%</option>
            <option value="40-30-30">40%-30%-30%</option>
            <option value="milestone_based">Milestone based</option>
            <option value="other">Other</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Submitting...' : 'Submit Project Intake'}
        </button>
      </form>
    </div>
  );
}
